import React, { useState, memo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

// India GeoJSON - verified working URL
const INDIA_GEO_URL = 'https://gist.githubusercontent.com/jbrobst/56c13bbbf9d97d187fea01ca62ea5112/raw/e388c4cae20aa53cb5090210a42ebb9b765c0a36/india_states.geojson';

/**
 * India Map Component for MBF Dashboard
 * Uses react-simple-maps for rendering India with location markers
 * 
 * @param {array} markers - Array of marker objects [{name, coordinates: [lng, lat], color}]
 */
const IndiaMap = ({ markers = [] }) => {
    const [scale, setScale] = useState(1000);

    // Default markers with Indian city coordinates [longitude, latitude]
    const defaultMarkers = [
        { name: 'Delhi', coordinates: [77.1025, 28.7041], color: '#4ECDC4' },      // Teal
        { name: 'Mumbai', coordinates: [72.8777, 19.076], color: '#FF6B6B' },      // Coral Red
        { name: 'Bangalore', coordinates: [77.5946, 12.9716], color: '#45B7D1' },  // Sky Blue
        { name: 'Chennai', coordinates: [80.2707, 13.0827], color: '#96CEB4' },    // Sage Green
        { name: 'Kolkata', coordinates: [88.3639, 22.5726], color: '#9B59B6' },    // Purple
        { name: 'Hyderabad', coordinates: [78.4867, 17.385], color: '#F39C12' },   // Orange
        { name: 'Jaipur', coordinates: [75.7873, 26.9124], color: '#E74C3C' },     // Red
        { name: 'Ahmedabad', coordinates: [72.5714, 23.0225], color: '#3498DB' },  // Blue
        { name: 'Pune', coordinates: [73.8567, 18.5204], color: '#2ECC71' },       // Green
    ];

    const displayMarkers = markers.length > 0 ? markers : defaultMarkers;

    const handleZoomIn = () => {
        setScale((prev) => Math.min(prev + 200, 2000));
    };

    const handleZoomOut = () => {
        setScale((prev) => Math.max(prev - 200, 600));
    };

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Map Container */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: '350px',
                }}
            >
                <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{
                        scale: scale,
                        center: [78.9629, 22.5937], // Center of India
                    }}
                    style={{
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {/* Render the Map (States) */}
                    <Geographies geography={INDIA_GEO_URL}>
                        {({ geographies }) =>
                            geographies.map((geo) => (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    style={{
                                        default: {
                                            fill: '#f3f3f3ff',
                                            stroke: '#0ea5e9',
                                            strokeWidth: 0.75,
                                            outline: 'none',
                                        },
                                        hover: {
                                            fill: '#80deea',
                                            stroke: '#0284c7',
                                            strokeWidth: 1,
                                            outline: 'none',
                                        },
                                        pressed: {
                                            fill: '#4dd0e1',
                                            stroke: '#0ea5e9',
                                            outline: 'none',
                                        },
                                    }}
                                />
                            ))
                        }
                    </Geographies>

                    {/* Render the Custom Markers - Teardrop Pin Design */}
                    {displayMarkers.map(({ name, coordinates, color }) => (
                        <Marker key={name} coordinates={coordinates}>
                            <Tooltip title={name} arrow placement="top">
                                <g
                                    transform="translate(-10, -28)"
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* Teardrop Pin Shape */}
                                    <path
                                        d="M10 0C4.477 0 0 4.477 0 10c0 7.5 10 18 10 18s10-10.5 10-18c0-5.523-4.477-10-10-10z"
                                        fill={color || '#4ECDC4'}
                                    />
                                    {/* Inner White Circle */}
                                    <circle
                                        cx="10"
                                        cy="10"
                                        r="4"
                                        fill="#ffffff"
                                    />
                                    {/* Shadow Line */}
                                    <ellipse
                                        cx="10"
                                        cy="29"
                                        rx="4"
                                        ry="1.5"
                                        fill="rgba(0,0,0,0.15)"
                                    />
                                </g>
                            </Tooltip>
                        </Marker>
                    ))}
                </ComposableMap>
            </Box>

            {/* Zoom Controls - Bottom Right */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '16px',
                    right: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                    padding: '2px 4px',
                }}
            >
                <IconButton
                    size="small"
                    onClick={handleZoomOut}
                    sx={{
                        width: '28px',
                        height: '28px',
                        color: '#666666',
                        '&:hover': { bgcolor: '#f3f4f6' },
                    }}
                >
                    <RemoveIcon sx={{ fontSize: '18px' }} />
                </IconButton>

                <Box sx={{ width: '1px', height: '20px', bgcolor: '#f2f2f2', mx: 0.5 }} />

                <IconButton
                    size="small"
                    onClick={handleZoomIn}
                    sx={{
                        width: '28px',
                        height: '28px',
                        color: '#666666',
                        '&:hover': { bgcolor: '#f3f4f6' },
                    }}
                >
                    <AddIcon sx={{ fontSize: '18px' }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default memo(IndiaMap);
